import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import ApexCharts from "apexcharts";
import jsPDF from "jspdf";
import api from "../../../../../api";
import autoTable from "jspdf-autotable";


const ResourceAreaChart = () => {
  const [resourceType, setResourceType] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [chartData, setChartData] = useState({
    labels: [],
    data: [],
    rawData: null,
  });
  const [noData, setNoData] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const params = { resource_type: resourceType };
        if (startDate) {
          params.start_date = startDate;
          params.end_date = endDate || new Date().toISOString().split("T")[0];
        }

        const response = await api.get("/ems/resource-utilization", {
          params,
        });

        if (response.data && response.data.labels && response.data.labels.length > 0) {
          let data;

          switch (resourceType) {
            case "employee":
              data = response.data.employees;
              break;
            case "vehicle":
              data = response.data.vehicles;
              break;
            default:
              data = response.data.combined;
          }

          setChartData({
            labels: response.data.labels,
            data: data,
            rawData: response.data,
          });

          const allZero = data.every((v) => v === 0);
          setNoData(allZero);
        } else {
          setChartData({ labels: [], data: [], rawData: null });
          setNoData(true);
        }
      } catch (error) {
        console.error("Error fetching resource utilization data:", error);
        setChartData({ labels: [], data: [], rawData: null });
        setNoData(true);
      }
    };

    fetchData();
  }, [resourceType, startDate, endDate]);

  const addDataTableToPDF = (doc, chartData, startY, pageWidth, pageHeight, margin, resourceType) => {
    if (!chartData.labels.length || !chartData.data.length) {
      return startY;
    }

    const tableWidth = pageWidth - (margin * 2);
    const rowHeight = 20;
    const headerHeight = 25;
    const cellPadding = 8;

    let currentY = startY;

    // Table title
    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text("Detailed Data Table", margin, currentY);
    currentY += 15;

    // Check if we need a new page before starting table
    if (currentY + headerHeight + (chartData.data.length * rowHeight) > pageHeight - margin) {
      doc.addPage();
      currentY = margin;

      // Add consistent header on new page
      doc.setFont("helvetica", "normal");
      doc.setFontSize(16);
      doc.setTextColor(0, 0, 0);
      doc.text(
        `Resource Utilization - ${
          resourceType === "all" ? "All Resources" : resourceType === "vehicle" ? "Vehicles" : "Employees"
        }`,
        pageWidth / 2,
        40,
        { align: "center" }
      );
    }

    // Table headers with background
    doc.setFillColor(240, 240, 240);
    doc.rect(margin, currentY, tableWidth, headerHeight, "F");

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);

    const dateColWidth = tableWidth * 0.6;
    const valueColWidth = tableWidth * 0.4;

    doc.text("Date", margin + cellPadding, currentY + headerHeight / 2 + 3);
    doc.text("Utilization", margin + dateColWidth + cellPadding, currentY + headerHeight / 2 + 3);

    doc.setDrawColor(200, 200, 200);
    doc.rect(margin, currentY, tableWidth, headerHeight);
    doc.rect(margin + dateColWidth, currentY, valueColWidth, headerHeight);

    currentY += headerHeight;

    // Table rows
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);

    chartData.labels.forEach((label, index) => {
      if (currentY + rowHeight > pageHeight - margin) {
        doc.addPage();
        currentY = margin;

        doc.setFont("helvetica", "normal");
        doc.setFontSize(16);
        doc.setTextColor(0, 0, 0);
        doc.text(
          `Resource Utilization - ${
            resourceType === "all" ? "All Resources" : resourceType === "vehicle" ? "Vehicles" : "Employees"
          }`,
          pageWidth / 2,
          40,
          { align: "center" }
        );

        doc.setFillColor(240, 240, 240);
        doc.rect(margin, currentY, tableWidth, headerHeight, "F");
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.text("Date", margin + cellPadding, currentY + headerHeight / 2 + 3);
        doc.text("Utilization", margin + dateColWidth + cellPadding, currentY + headerHeight / 2 + 3);
        doc.setDrawColor(200, 200, 200);
        doc.rect(margin, currentY, tableWidth, headerHeight);
        doc.rect(margin + dateColWidth, currentY, valueColWidth, headerHeight);
        currentY += headerHeight;
      }

      if (index % 2 === 0) {
        doc.setFillColor(250, 250, 250);
        doc.rect(margin, currentY, tableWidth, rowHeight, "F");
      }

      const formattedDate = new Date(label).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });

      doc.setTextColor(0, 0, 0);
      doc.text(formattedDate, margin + cellPadding, currentY + rowHeight / 2 + 3);
      doc.text(chartData.data[index].toString(), margin + dateColWidth + cellPadding, currentY + rowHeight / 2 + 3);

      doc.setDrawColor(200, 200, 200);
      doc.rect(margin, currentY, tableWidth, rowHeight);
      doc.rect(margin + dateColWidth, currentY, valueColWidth, rowHeight);

      currentY += rowHeight;
    });

    return currentY;
  };

  const exportPDF = async () => {
  try {
    const dataURI = await ApexCharts.exec("resource-area-chart", "dataURI", {
      scale: 2,
      width: 800,
    });

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 10;

    const title =
      resourceType === "all"
        ? "All Resources"
        : resourceType === "vehicle"
        ? "Vehicles"
        : "Employees";

    const periodText =
      startDate && endDate
        ? `Period: ${startDate} to ${endDate}`
        : startDate
        ? `From: ${startDate}`
        : "";

    // Add chart image at top
    const chartHeight = 100;
    const chartTopMargin = 40;
    doc.addImage(dataURI.imgURI, "PNG", 10, chartTopMargin, 190, chartHeight);

    // Track first page
    let firstPage = true;

    const tableOptions = {
      startY: chartTopMargin + chartHeight + 20,
      head: [["Date", "Utilization (%)"]],
      body: chartData.labels.map((label, index) => {
        const formattedDate = new Date(label).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
        return [formattedDate, chartData.data[index]?.toFixed(2) || "0.00"];
      }),
      theme: "grid",
      headStyles: { fillColor: [0, 123, 255], textColor: 255, fontStyle: "bold" },
      styles: { fontSize: 10, cellPadding: 3 },
      margin: { left: margin, right: margin },
      didDrawPage: (data) => {
        // Only show heading on the first page
        if (firstPage) {
          doc.setFontSize(16);
          doc.setFont("helvetica", "bold");
          doc.text(`Resource Utilization - ${title}`, pageWidth / 2, 20, { align: "center" });

          if (periodText) {
            doc.setFontSize(10);
            doc.setFont("helvetica", "normal");
            doc.text(periodText, pageWidth / 2, 30, { align: "center" });
          }

          firstPage = false; // Heading only on first page
        }

        // Footer with page number
        doc.setFontSize(8);
        doc.text(
          `Page ${data.pageNumber} of ${doc.internal.getNumberOfPages()}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: "center" }
        );
      },
    };

    if (chartData.labels.length > 0 && !noData) {
      autoTable(doc, tableOptions);
    } else {
      doc.setFontSize(10);
      doc.text(
        "No data available for the selected period.",
        pageWidth / 2,
        chartTopMargin + chartHeight + 20,
        { align: "center" }
      );
    }

    doc.save(`resource_utilization_${resourceType}.pdf`);
  } catch (error) {
    console.error("Error exporting PDF:", error);
  }
};


  const downloadGraph = async () => {
  try {
    // Get chart as image (slightly lower scale for smaller file size)
    const dataURI = await ApexCharts.exec("resource-area-chart", "dataURI", {
      scale: 1.2, // Smaller than default (2)
      width: 700, // Restrict width for smaller output
    });

    // Create image element
    const chartImage = new Image();
    chartImage.src = dataURI.imgURI;

    // Wait until image loads before drawing
    await new Promise((resolve, reject) => {
      chartImage.onload = resolve;
      chartImage.onerror = reject;
    });

    // Canvas setup
    const chartWidth = 700;
    const aspectRatio = chartImage.width / chartImage.height;
    const chartHeight = chartWidth / aspectRatio;
    const headingHeight = 50;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    // Set smaller canvas size (reduces PNG size)
    canvas.width = chartWidth;
    canvas.height = chartHeight + headingHeight;

    // Background
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Heading text
    ctx.fillStyle = "#000000";
    ctx.font = `bold 18px "Plus Jakarta Sans", sans-serif`;
    ctx.textAlign = "center";
    ctx.fillText(
      `Resource Utilization - ${
        resourceType === "all"
          ? "All Resources"
          : resourceType === "vehicle"
          ? "Vehicles"
          : "Employees"
      }`,
      canvas.width / 2,
      30
    );

    // Draw chart image (fit inside reduced canvas)
    ctx.drawImage(chartImage, 0, headingHeight, chartWidth, chartHeight);

    // Convert to PNG and trigger download
    const imageData = canvas.toDataURL("image/png"); // don't pass quality arg (PNG ignores it)
    const link = document.createElement("a");
    link.href = imageData;
    link.download = `resource_utilization_${resourceType}.png`;
    link.click();
  } catch (error) {
    console.error("Error downloading graph:", error);
    alert("Failed to download graph image.");
  }
};


  const options = {
    chart: {
      id: "resource-area-chart",
      fontFamily: "Plus Jakarta Sans, sans-serif",
      height: 350,
      type: "area",
      toolbar: { show: false },
    },
    grid: {
      borderColor: "#f1f1f1",
      strokeDashArray: 3,
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: true } },
    },
    colors: ["#5C9FFF"],
    dataLabels: { enabled: false },
    stroke: { curve: "smooth", width: 2 },
    xaxis: {
      type: "datetime",
      categories: chartData.labels,
      labels: {
        style: { colors: "#6b7280", fontSize: "12px" },
        datetimeFormatter: { month: "MMM" },
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        style: { colors: "#6b7280", fontSize: "12px" },
        formatter: (val) => val.toFixed(0),
      },
    },
    tooltip: { x: { format: "dd MMM yyyy" }, theme: "light" },
    fill: {
      type: "gradient",
      gradient: { shadeIntensity: 1, opacityFrom: 0.7, opacityTo: 0.3, stops: [0, 90, 100] },
    },
  };

  const series = [
    {
      name:
        resourceType === "all"
          ? "All Resources"
          : resourceType === "vehicle"
          ? "Vehicles"
          : "Employees",
      data: chartData.data,
    },
  ];

  return (
    <>
    <style>{`
    @media(max-width: 575px) {
      .filter-panel{flex-wrap: wrap !important;}
      .filter-left-panel {width: 100%;}
      .filter-left-panel .form-group{width: 100%;}
      .export-btn{width: 100%; }
      .export-btn .btn{width: 100%;}
    }
    `}</style>
      <div className="filter-panel  d-flex justify-content-between mb-4 flex-wrap gap-2">
        <div className="d-flex gap-2 flex-wrap align-items-center filter-left-panel">
          <div className="form-group mb-0">
            <lable>Status</lable>
            <select
              className="form-select"
              value={resourceType}
              onChange={(e) => setResourceType(e.target.value)}
              style={{ minWidth: "120px" }}
            >
              <option value="all">All Resources</option>
              <option value="vehicle">Vehicles</option>
              <option value="employee">Employees</option>
            </select>
          </div>
          <div className="form-group mb-0">
            <lable>Start Date</lable>
            <input
              type="date"
              className="form-control"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              placeholder="Start Date"
            />
          </div>
          <div className="form-group mb-0">
            <lable>End Date</lable>
            <input
              type="date"
              className="form-control"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              placeholder="End Date"
            />
          </div>
        </div>

        <div className="dropdown export-btn">
          <button
            className="btn btn-sm btn-outline-primary dropdown-toggle"
            type="button"
            id="exportDropdown"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            Export Options
          </button>
          <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="exportDropdown">
            <li>
              <button className="dropdown-item" onClick={exportPDF}>
                Export PDF
              </button>
            </li>
            <li>
              <button className="dropdown-item" onClick={downloadGraph}>
                Download Graph
              </button>
            </li>
          </ul>
        </div>
      </div>

      {noData ? (
        <div className="text-center mt-5 py-5 fw-semibold text-muted">
          No utilization data available for the selected period
        </div>
      ) : (
        <div id="resource-area-spline">
          <Chart options={options} series={series} type="area" height={350} />
        </div>
      )}

      {chartData.data.length > 0 && (
        <div className="d-flex align-items-center justify-content-center mt-3">
          <span className="round-8 bg-primary rounded-circle me-2 d-inline-block"></span>
          <span className="text-muted">
            {resourceType === "all"
              ? "All Resources"
              : resourceType === "vehicle"
              ? "Vehicles"
              : "Employees"}
          </span>
        </div>
      )}
    </>
  );
};

export default ResourceAreaChart;